import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FuseConfigService } from '@fuse/services/config.service';
import { SUPPORT_EMAIL } from 'app/modules/global/constants';
import { AuthRepositoryService } from 'app/repositories/auth-repository.service';
import { Language } from 'app/services/i18n.languages';
import { I18nService } from 'app/services/i18n.service';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { FuseInnerScrollDirective } from '../../../../../@fuse/directives/fuse-inner-scroll/fuse-inner-scroll.directive';
import { MatFormField, MatInput, MatError } from '@angular/material/input';
import { ButtonTypeDirective } from '../../../../modules/global/directives/button-type.directive';
import { MatButton } from '@angular/material/button';
import { SubmitDirective } from '../../../../modules/global/directives/submit.directive';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { TranslatePipe, _ } from '@ngx-translate/core';
import { ThemeService } from 'app/modules/layout/components/toolbar/theme.service';
import { GlobalErrorsDirective } from 'app/modules/global/directives/form-validation/global-errors.directive';
import { GlobalDisabledDirective } from 'app/modules/global/directives/form-validation/global-disabled.directive';

@Component({
  selector: 'forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FuseInnerScrollDirective,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatError,
    ButtonTypeDirective,
    MatButton,
    SubmitDirective,
    RouterLink,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    TranslatePipe,
    GlobalErrorsDirective,
    GlobalDisabledDirective,
  ],
})
export class ForgotPasswordComponent implements OnInit {
  private readonly submitButton = viewChild('submitButton', { read: ElementRef });

  forgotPasswordForm: FormGroup;

  readonly ThemeService = inject(ThemeService);
  readonly SUPPORT_EMAIL = SUPPORT_EMAIL;

  readonly languages: Language[] = environment.languages ? I18nService.languages || [] : [];
  readonly currentLanguage = computed(() => this._i18nService.currentLanguage);

  private readonly _fuseConfigService = inject(FuseConfigService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _authRepositoryService = inject(AuthRepositoryService);
  private readonly _i18nService = inject(I18nService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _destroyRef = inject(DestroyRef);

  constructor() {
    // Configure the layout
    this._fuseConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        toolbar: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        sidepanel: {
          hidden: true,
        },
      },
    };
  }

  setLanguage(lang: Language): void {
    if (lang.code !== this._i18nService.currentLanguageCode()) {
      this._i18nService.setLanguage(lang.code);
      this._toastrService.success(this._i18nService.translate(_('Nuova lingua: {language}'), { language: lang.name }));
    }
  }

  ngOnInit(): void {
    this._activatedRoute.queryParams
      .pipe(
        mergeMap((params) => {
          if (params.token) {
            return this._authRepositoryService.confirmationResetPassword(params.token).pipe(
              map((response) => {
                return {
                  data: response,
                };
              }),
            );
          } else {
            return of([]).pipe(
              map((response) => {
                return { data: response };
              }),
            );
          }
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((response: any) => {
        if (response.data.username && !response.data.error_send_mail) {
          this._router.navigate(['login']);
        }
      });

    this.forgotPasswordForm = this._formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  keypressEvent(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.submitButton()?.nativeElement.click();
    }
  }

  public onSubmit = () => this._authRepositoryService.recoverPassword(this.forgotPasswordForm.get('email').value);

  emailSent = signal(false);

  beforeSubmit() {
    this.emailSent.set(true);
  }

  afterSubmit(response) {
    setTimeout(() => {
      this._router.navigate(['login']);
    }, 1200);
  }
}
